// Shared live-microphone store. One Web Audio graph, read by the 3D scenes
// (imperatively, every frame) and by the HUD toggle button (via React subscribe).
// Kept outside React so the Canvas scenes and the DOM HUD can use the same stream
// without threading refs through <ScrollControls>.

import { useSyncExternalStore } from 'react'

const FFT_SIZE = 1024

let ctx = null
let analyser = null
let stream = null
let timeData = null // Uint8Array, time-domain samples 0..255 (centre 128)
let freqData = null // Uint8Array, frequency magnitudes 0..255 (already dB-scaled)

const state = { active: false, pending: false, error: null }
const listeners = new Set()

function emit() {
  for (const l of listeners) l()
}

export async function enableMic() {
  if (state.active || state.pending) return
  state.pending = true
  state.error = null
  emit()
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    ctx = new (window.AudioContext || window.webkitAudioContext)()
    const source = ctx.createMediaStreamSource(stream)
    analyser = ctx.createAnalyser()
    analyser.fftSize = FFT_SIZE
    analyser.smoothingTimeConstant = 0.6
    source.connect(analyser)
    timeData = new Uint8Array(analyser.fftSize)
    freqData = new Uint8Array(analyser.frequencyBinCount)
    state.active = true
  } catch (err) {
    state.error = err?.name === 'NotAllowedError' ? 'Mic permission denied' : 'Mic unavailable'
    disableMic()
  } finally {
    state.pending = false
    emit()
  }
}

export function disableMic() {
  stream?.getTracks().forEach((t) => t.stop())
  ctx?.close()
  ctx = null
  analyser = null
  stream = null
  timeData = null
  freqData = null
  state.active = false
  emit()
}

export function toggleMic() {
  state.active ? disableMic() : enableMic()
}

// Imperative read for the render loop. Returns the live time-domain buffer
// (values 0..255, centre 128) or null when the mic is off.
export function getMicTimeData() {
  if (!analyser || !timeData) return null
  analyser.getByteTimeDomainData(timeData)
  return timeData
}

// Imperative read for the render loop. Returns the live frequency magnitudes
// (Uint8Array, 0..255, already on a dB scale by Web Audio) or null when off.
// Index i covers frequency i * sampleRate / fftSize.
export function getMicFreqData() {
  if (!analyser || !freqData) return null
  analyser.getByteFrequencyData(freqData)
  return freqData
}

// React subscription for UI (button label / error text).
export function useMicState() {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    () => state,
    () => state
  )
}
